/**
 * Cliente HTTP del panel (Vue).
 * Token en sessionStorage: sobrevive redirect, se borra al cerrar pestaña.
 */

const TOKEN_KEY = "centralizador_token";
const ROL_KEY = "centralizador_rol";

export class ApiError extends Error {
  /**
   * @param {number} status
   * @param {string} codigo
   * @param {string} mensaje
   */
  constructor(status, codigo, mensaje) {
    super(mensaje);
    this.name = "ApiError";
    this.status = status;
    this.codigo = codigo;
    this.mensaje = mensaje;
  }
}

export function obtenerToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function obtenerRol() {
  return sessionStorage.getItem(ROL_KEY);
}

export function guardarToken(token, rol) {
  sessionStorage.setItem(TOKEN_KEY, token);
  if (rol) sessionStorage.setItem(ROL_KEY, rol);
}

export function limpiarToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(ROL_KEY);
}

function cerrarSesion() {
  limpiarToken();
  window.location.href = "/login/";
}

function esRutaPerfilAdmin(path) {
  const ruta = path.split("?")[0];
  return ruta === "/api/admin/me" || ruta === "/api/admin/me/";
}

/** 401 del panel, o 404 de perfil si el comercio ya no está disponible. */
function esSesionInvalida(path, status, codigo) {
  if (status === 401) return true;
  return esRutaPerfilAdmin(path) && status === 404 && codigo === "no_encontrado";
}

export function haySesion() {
  return Boolean(obtenerToken());
}

export function rutaTrasLogin(rol) {
  return rol === "super" ? "/super/" : "/admin/";
}

/**
 * @param {unknown} body
 * @param {number} status
 */
function errorDesdeRespuesta(body, status) {
  if (
    body &&
    typeof body === "object" &&
    "error" in body &&
    body.error &&
    typeof body.error === "object"
  ) {
    const err = /** @type {{ codigo?: unknown, mensaje?: unknown }} */ (
      body.error
    );
    return new ApiError(
      status,
      typeof err.codigo === "string" ? err.codigo : "error_desconocido",
      typeof err.mensaje === "string"
        ? err.mensaje
        : "Error inesperado del servidor"
    );
  }
  return new ApiError(
    status,
    "error_desconocido",
    "Error inesperado del servidor"
  );
}

/**
 * @param {string} path
 * @param {RequestInit & { json?: unknown, formData?: FormData }} [opciones]
 */
export async function apiFetch(path, opciones = {}) {
  const { json, formData, headers: headersExtra, ...init } = opciones;
  const headers = new Headers(headersExtra || {});

  const requiereAuth =
    path.startsWith("/api/admin") || path.startsWith("/api/super");
  if (requiereAuth) {
    const token = obtenerToken();
    if (!token) {
      cerrarSesion();
      throw new ApiError(401, "no_autenticado", "Sesión no iniciada");
    }
    headers.set("Authorization", `Bearer ${token}`);
  }

  let body = init.body;
  if (formData instanceof FormData) {
    body = formData;
  } else if (json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(json);
  }

  const respuesta = await fetch(path, { ...init, headers, body });

  if (respuesta.status === 204) return null;

  const contentType = respuesta.headers.get("Content-Type") || "";
  const bodyParseado = contentType.includes("application/json")
    ? await respuesta.json()
    : null;

  if (!respuesta.ok) {
    const error = errorDesdeRespuesta(bodyParseado, respuesta.status);
    if (requiereAuth && esSesionInvalida(path, error.status, error.codigo)) {
      cerrarSesion();
    }
    throw error;
  }

  return bodyParseado;
}

/** POST /api/auth/login → { token, rol } */
export async function login(email, password) {
  const data = /** @type {{ token?: string, rol?: string }} */ (
    await apiFetch("/api/auth/login", {
      method: "POST",
      json: { email, password },
    })
  );

  if (!data || typeof data.token !== "string" || !data.token) {
    throw new ApiError(500, "respuesta_invalida", "Login sin token");
  }

  const rol = data.rol === "super" ? "super" : "comercio";
  guardarToken(data.token, rol);
  return { token: data.token, rol };
}

export function logout() {
  cerrarSesion();
}

/**
 * GET /api/admin/me
 * Comercio incluye operativos F-010: zona_horaria, ofrece_delivery,
 * zona_delivery, eta_minutos_delivery, horarios[{id,dia_semana,abre,cierra}].
 */
export async function obtenerMe() {
  const data = /** @type {{ comercio?: Record<string, unknown> }} */ (
    await apiFetch("/api/admin/me", { method: "GET" })
  );
  return data?.comercio ?? null;
}

/**
 * PATCH /api/admin/me
 * Campos operativos opcionales; si `horarios` está presente → replace-all.
 * Con ofrece_delivery=false el servidor nullifica zona y ETA.
 */
export async function actualizarPerfilComercio(payload) {
  const data = /** @type {{ comercio?: Record<string, unknown> }} */ (
    await apiFetch("/api/admin/me", { method: "PATCH", json: payload })
  );
  return data?.comercio ?? null;
}

/** POST /api/admin/logo — FormData con campo "logo" */
export async function subirLogoComercio(formData) {
  const data = /** @type {{ comercio?: Record<string, unknown> }} */ (
    await apiFetch("/api/admin/logo", { method: "POST", formData })
  );
  return data?.comercio ?? null;
}

/** GET /api/admin/categorias */
export async function listarCategorias() {
  const data = /** @type {{ categorias?: unknown[] }} */ (
    await apiFetch("/api/admin/categorias", { method: "GET" })
  );
  return Array.isArray(data?.categorias) ? data.categorias : [];
}

export async function crearCategoria(payload) {
  return apiFetch("/api/admin/categorias", { method: "POST", json: payload });
}

export async function actualizarCategoria(id, payload) {
  return apiFetch(`/api/admin/categorias/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    json: payload,
  });
}

export async function eliminarCategoria(id) {
  await apiFetch(`/api/admin/categorias/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
  });
}

/** GET /api/admin/menu/categorias */
export async function listarCategoriasMenu() {
  const data = /** @type {{ categorias?: unknown[] }} */ (
    await apiFetch("/api/admin/menu/categorias", { method: "GET" })
  );
  return Array.isArray(data?.categorias) ? data.categorias : [];
}

export async function crearCategoriaMenu(payload) {
  return apiFetch("/api/admin/menu/categorias", {
    method: "POST",
    json: payload,
  });
}

export async function actualizarCategoriaMenu(id, payload) {
  return apiFetch(
    `/api/admin/menu/categorias/${encodeURIComponent(String(id))}`,
    { method: "PUT", json: payload }
  );
}

export async function eliminarCategoriaMenu(id) {
  return apiFetch(
    `/api/admin/menu/categorias/${encodeURIComponent(String(id))}`,
    { method: "DELETE" }
  );
}

export async function reordenarCategoriaMenu(id, direccion) {
  return apiFetch(
    `/api/admin/menu/categorias/${encodeURIComponent(String(id))}/reordenar`,
    { method: "POST", json: { direccion } }
  );
}

/** GET /api/admin/menu/items */
export async function listarItemsMenu() {
  const data = /** @type {{ items?: unknown[] }} */ (
    await apiFetch("/api/admin/menu/items", { method: "GET" })
  );
  return Array.isArray(data?.items) ? data.items : [];
}

export async function crearItemMenu(payload) {
  return apiFetch("/api/admin/menu/items", { method: "POST", json: payload });
}

export async function actualizarItemMenu(id, payload) {
  return apiFetch(
    `/api/admin/menu/items/${encodeURIComponent(String(id))}`,
    { method: "PUT", json: payload }
  );
}

export async function eliminarItemMenu(id) {
  return apiFetch(
    `/api/admin/menu/items/${encodeURIComponent(String(id))}`,
    { method: "DELETE" }
  );
}

export async function reordenarItemMenu(id, direccion) {
  return apiFetch(
    `/api/admin/menu/items/${encodeURIComponent(String(id))}/reordenar`,
    { method: "POST", json: { direccion } }
  );
}

export async function fijarDisponibleItemMenu(id, disponible) {
  return apiFetch(
    `/api/admin/menu/items/${encodeURIComponent(String(id))}/disponible`,
    { method: "PATCH", json: { disponible: Boolean(disponible) } }
  );
}

/** GET /api/admin/productos?seccion=imagenes|promociones */
export async function listarProductos(seccion) {
  const data = /** @type {{ productos?: unknown[], cupo?: { usados?: number, max?: number | null }, seccion?: string }} */ (
    await apiFetch(
      `/api/admin/productos?seccion=${encodeURIComponent(String(seccion))}`,
      { method: "GET" }
    )
  );
  return {
    productos: Array.isArray(data?.productos) ? data.productos : [],
    cupo: data?.cupo ?? { usados: 0, max: null },
    seccion: data?.seccion ?? seccion,
  };
}

export async function crearProducto(formData) {
  const data = /** @type {{ producto?: unknown }} */ (
    await apiFetch("/api/admin/productos", { method: "POST", formData })
  );
  return data?.producto ?? null;
}

export async function actualizarProducto(id, formData) {
  const data = /** @type {{ producto?: unknown }} */ (
    await apiFetch(`/api/admin/productos/${encodeURIComponent(String(id))}`, {
      method: "PUT",
      formData,
    })
  );
  return data?.producto ?? null;
}

export async function eliminarProducto(id) {
  await apiFetch(`/api/admin/productos/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
  });
}

export async function listarPlanes() {
  const data = /** @type {{ planes?: unknown[] }} */ (
    await apiFetch("/api/super/planes", { method: "GET" })
  );
  return Array.isArray(data?.planes) ? data.planes : [];
}

export async function listarPlantillas() {
  const data = /** @type {{ plantillas?: unknown[] }} */ (
    await apiFetch("/api/super/plantillas", { method: "GET" })
  );
  return Array.isArray(data?.plantillas) ? data.plantillas : [];
}

export async function crearPlantilla(payload) {
  return apiFetch("/api/super/plantillas", { method: "POST", json: payload });
}

export async function actualizarPlantilla(id, payload) {
  return apiFetch(`/api/super/plantillas/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    json: payload,
  });
}

export async function eliminarPlantilla(id) {
  await apiFetch(`/api/super/plantillas/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
  });
}

export async function crearPlantillaCategoria(plantillaId, payload) {
  return apiFetch(
    `/api/super/plantillas/${encodeURIComponent(String(plantillaId))}/categorias`,
    { method: "POST", json: payload }
  );
}

export async function actualizarPlantillaCategoria(plantillaId, catId, payload) {
  return apiFetch(
    `/api/super/plantillas/${encodeURIComponent(String(plantillaId))}/categorias/${encodeURIComponent(String(catId))}`,
    { method: "PUT", json: payload }
  );
}

export async function eliminarPlantillaCategoria(plantillaId, catId) {
  await apiFetch(
    `/api/super/plantillas/${encodeURIComponent(String(plantillaId))}/categorias/${encodeURIComponent(String(catId))}`,
    { method: "DELETE" }
  );
}

export async function listarClientes() {
  const data = /** @type {{ clientes?: unknown[] }} */ (
    await apiFetch("/api/super/clientes", { method: "GET" })
  );
  return Array.isArray(data?.clientes) ? data.clientes : [];
}

export async function crearCliente(payload) {
  return apiFetch("/api/super/clientes", { method: "POST", json: payload });
}

export async function actualizarCliente(id, payload) {
  return apiFetch(`/api/super/clientes/${encodeURIComponent(String(id))}`, {
    method: "PUT",
    json: payload,
  });
}

export async function eliminarCliente(id) {
  return apiFetch(`/api/super/clientes/${encodeURIComponent(String(id))}`, {
    method: "DELETE",
  });
}

export async function subirLogoCliente(id, formData) {
  return apiFetch(
    `/api/super/clientes/${encodeURIComponent(String(id))}/logo`,
    { method: "POST", formData }
  );
}

export async function listarUsuariosCliente(clienteId) {
  const data = /** @type {{ usuarios?: unknown[] }} */ (
    await apiFetch(
      `/api/super/clientes/${encodeURIComponent(String(clienteId))}/usuarios`,
      { method: "GET" }
    )
  );
  return Array.isArray(data?.usuarios) ? data.usuarios : [];
}

export async function crearUsuarioCliente(clienteId, payload) {
  return apiFetch(
    `/api/super/clientes/${encodeURIComponent(String(clienteId))}/usuarios`,
    { method: "POST", json: payload }
  );
}
